import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {firstValueFrom, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {PagedApiResponse} from '../models/clare';
import {EnvironmentConfiguration} from './core';

export enum HttpMethod {
  DELETE = 'DELETE',
  GET = 'GET',
  PUT = 'PUT',
  PATCH = 'PATCH',
  POST = 'POST',
}

export interface ApiRequestOptions {
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  responseType?: never;
  withCredentials?: boolean;
  body?: any;
}

export const objectToHttpParams = (params: object): {[key: string]: string} => {
  const httpParams: {[key: string]: string} = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      httpParams[key] = String(value);
    }
  });
  return httpParams;
};

export const pluckResult =
  <T>() =>
  (source: Observable<{result: T}>) =>
    source.pipe(map(resp => resp.result));

export interface ApiService {
  GET<T>(url: string, options?: ApiRequestOptions): Observable<T>;

  PATCH<T>(url: string, data: unknown, options?: ApiRequestOptions): Observable<T>;

  POST<T>(url: string, data: unknown, options?: ApiRequestOptions): Observable<T>;

  PUT<T>(url: string, data: unknown, options?: ApiRequestOptions): Observable<T>;

  DELETE<T>(url: string, options?: ApiRequestOptions): Observable<T>;
}

export abstract class ProductManagementApiService implements ApiService {
  readonly apiUrl: string;
  abstract readonly controllerPath: string;

  protected constructor(protected http: HttpClient) {
    this.apiUrl = EnvironmentConfiguration.commerceApiUri();
  }

  DELETE<T>(url: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(url, options);
  }

  GET<T>(url: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.get<T>(url, options);
  }

  PATCH<T>(url: string, data: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.patch<T>(url, data, options);
  }

  PUT<T>(url: string, data: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.put<T>(url, data, options);
  }

  POST<TReturn>(url: string, data: unknown, options?: ApiRequestOptions): Observable<TReturn> {
    return this.http.post<TReturn>(url, data, options);
  }

  requestOptions(): ApiRequestOptions {
    return {
      headers: {
        Accept: 'application/json',
      },
      withCredentials: true,
    };
  }

  protected getUrl(path = ''): string {
    return this.apiUrl + path;
  }

  protected async exhaustPagedResults<T>(url: string): Promise<T[]> {
    const result: T[] = [];
    let page = await firstValueFrom(this.GET<PagedApiResponse<T>>(`${url}?pageSize=100`));
    result.push(...page.result);

    while ((page.metadata.pagination?.current_page ?? 0) < (page.metadata.pagination?.total_pages ?? 0)) {
      url = `${url}?pageSize=100&page=${(page.metadata.pagination?.current_page ?? 0) + 1}`;
      page = await firstValueFrom(this.GET<PagedApiResponse<T>>(url));
      result.push(...page.result);
    }

    return result;
  }
}
